import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { ShieldX } from 'lucide-react';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  confirmPasswordReset as fbConfirmPasswordReset,
  createUserWithEmailAndPassword,
  deleteUser,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendEmailVerification as fbSendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  updatePassword as fbUpdatePassword,
  updateProfile,
  type User,
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import { reportReferralConversion } from '../lib/referral';

// =============================================================================
// Context value
// =============================================================================

interface AuthContextValue {
  user: User | null;
  /** True while the persisted session is being resolved on first load. */
  loading: boolean;
  isAuthenticated: boolean;
  /** True only when signed in AND the email address is verified. */
  isVerified: boolean;
  /** True when the user carries the `admin: true` custom claim. */
  isAdmin: boolean;

  signUp: (email: string, password: string, displayName?: string) => Promise<User>;
  signIn: (email: string, password: string) => Promise<User>;
  signInWithGoogle: () => Promise<User>;
  signOut: () => Promise<void>;

  resetPassword: (email: string) => Promise<void>;
  confirmPasswordReset: (oobCode: string, newPassword: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;

  sendEmailVerification: () => Promise<void>;
  reloadUser: () => Promise<void>;

  reauthenticate: (password: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Persist the session across reloads and keep `user` in sync.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (next) => {
      setUser(next);
      // Read the `admin` custom claim from the signed ID token.
      if (next) {
        try {
          const { claims } = await next.getIdTokenResult();
          setIsAdmin(claims.admin === true);
        } catch {
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      isVerified: !!user?.emailVerified,
      isAdmin,

      signUp: async (email, password, displayName) => {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        if (displayName) await updateProfile(cred.user, { displayName });
        // Fire off the verification email immediately after sign-up.
        await fbSendEmailVerification(cred.user);
        reportReferralConversion('signup').catch(() => undefined);
        return cred.user;
      },

      signIn: async (email, password) => {
        const cred = await signInWithEmailAndPassword(auth, email, password);
        return cred.user;
      },

      signInWithGoogle: async () => {
        const provider = new GoogleAuthProvider();
        const cred = await signInWithPopup(auth, provider);
        return cred.user;
      },

      signOut: () => firebaseSignOut(auth),

      resetPassword: (email) => sendPasswordResetEmail(auth, email),

      confirmPasswordReset: (oobCode, newPassword) =>
        fbConfirmPasswordReset(auth, oobCode, newPassword),

      updatePassword: async (newPassword) => {
        if (!auth.currentUser) throw new Error('You must be signed in to change your password.');
        await fbUpdatePassword(auth.currentUser, newPassword);
      },

      sendEmailVerification: async () => {
        if (!auth.currentUser) throw new Error('You must be signed in to verify your email.');
        await fbSendEmailVerification(auth.currentUser);
      },

      reloadUser: async () => {
        if (!auth.currentUser) return;
        await auth.currentUser.reload();
        // reload() mutates the same instance, so React wouldn't see a change.
        // Clone preserving the prototype (methods) + getters to get a fresh ref.
        const u = auth.currentUser;
        setUser(Object.create(Object.getPrototypeOf(u), Object.getOwnPropertyDescriptors(u)) as User);
      },

      reauthenticate: async (password) => {
        const current = auth.currentUser;
        if (!current?.email) throw new Error('No signed-in user to re-authenticate.');
        const credential = EmailAuthProvider.credential(current.email, password);
        await reauthenticateWithCredential(current, credential);
      },

      deleteAccount: async () => {
        if (!auth.currentUser) throw new Error('You must be signed in to delete your account.');
        await deleteUser(auth.currentUser);
      },
    }),
    [user, loading, isAdmin],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

// =============================================================================
// Route guards
// =============================================================================

function AuthLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <div
        className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600"
        role="status"
        aria-label="Loading"
      />
    </div>
  );
}

interface RouteGuardProps {
  children?: ReactNode;
  redirectTo?: string;
}

/**
 * Renders children (or `<Outlet />`) only when a user is signed in. While
 * Firebase resolves the persisted session, shows a loader so we don't flash the
 * login page for already-authenticated users. Email-verification gating is
 * handled separately by `<RequireVerifiedEmail>`.
 */
export function ProtectedRoute({ children, redirectTo = '/login' }: RouteGuardProps) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) return <AuthLoading />;
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }
  return <>{children ?? <Outlet />}</>;
}

/** Inverse of `ProtectedRoute` — keeps signed-in users out of /login, /signup. */
export function PublicOnlyRoute({ children, redirectTo = '/occupation' }: RouteGuardProps) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) return <AuthLoading />;
  if (isAuthenticated) {
    const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;
    return <Navigate to={from ?? redirectTo} replace />;
  }
  return <>{children ?? <Outlet />}</>;
}

/**
 * Guards the website-administrator section. Requires sign-in AND the `admin`
 * custom claim. Non-admins get a clear "not authorized" screen rather than a
 * silent redirect. Firestore rules enforce the same claim server-side, so this
 * is UX — not the security boundary.
 */
export function AdminRoute({ children }: RouteGuardProps) {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) return <AuthLoading />;
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from: location }} />;
  if (!isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
        <div className="max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm sm:p-10">
          <span className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-rose-50 text-rose-600">
            <ShieldX className="h-8 w-8" />
          </span>
          <h1 className="text-xl font-bold text-gray-900">Administrators only</h1>
          <p className="mt-2 text-sm text-gray-500">
            You don't have access to this area. If you should, ask an admin to grant your account access.
          </p>
          <Link to="/occupation" className="mt-6 inline-block font-semibold text-blue-600 hover:underline">
            Back to app
          </Link>
        </div>
      </div>
    );
  }
  return <>{children ?? <Outlet />}</>;
}
